import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { healthcareApi, type InterviewRecord, type InterviewRecordOptionsResponse } from '../api/client';
import { formatFollowDate, formatRecordedAt, NEXT_FOLLOW_NEEDED, recordValueLabel } from '../utils/interviewRecords';

interface Props {
  personId: string;
  options: InterviewRecordOptionsResponse;
}

/**
 * 個人詳細（HC-S02）の「直近の面談記録」（課題 #71 / BE PR #143）。
 * 最新の 1 件だけを取り、実施日時・今後の対応方針・次回フォローを出す。
 * 今後の対応方針は保健師の方針で、フォロー状況とは連動しない（BE の項目の注記のとおり）。
 */
export default function LatestInterviewRecord({ personId, options }: Props) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [record, setRecord] = useState<InterviewRecord | null | undefined>(undefined);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    healthcareApi
      .listInterviewRecords(personId, token, 1)
      .then((res) => {
        if (!cancelled) setRecord(Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : null);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, personId]);

  const items = options.items;
  const toRecords = (
    <Link to={`/healthcare/person/${personId}/interview`}>{t('healthcare.records.toRecords')}</Link>
  );

  return (
    <div className="healthcare-card" data-testid="latest-interview-record">
      <h3>{t('healthcare.records.latestTitle')}</h3>
      {error ? (
        <p className="healthcare-error-text">{t('healthcare.records.latestError')}</p>
      ) : record === undefined ? (
        <p>{t('common.loading')}</p>
      ) : record === null ? (
        <p>{t('healthcare.records.empty')}</p>
      ) : (
        <dl className="interview-record-summary">
          <div>
            <dt>{t('healthcare.records.column.recordedAt')}</dt>
            <dd>{formatRecordedAt(record.recorded_at)}</dd>
          </div>
          <div>
            <dt>{t('healthcare.records.column.plan')}</dt>
            <dd>{recordValueLabel(items, 'plan_level', record.plan_level) || t('healthcare.records.notEntered')}</dd>
          </div>
          <div>
            <dt>{t('healthcare.records.column.nextFollow')}</dt>
            <dd>
              {record.next_follow_needed === NEXT_FOLLOW_NEEDED
                ? t('healthcare.records.nextFollowOn', { date: formatFollowDate(record.next_follow_date) })
                : recordValueLabel(items, 'next_follow_needed', record.next_follow_needed) || t('healthcare.records.notEntered')}
            </dd>
          </div>
        </dl>
      )}
      <p className="healthcare-records-link">{toRecords}</p>
    </div>
  );
}
